import {
  ArrowLeftRight,
  BadgeCheck,
  ChartPie,
  CircleHelp,
  CircleUser,
  CreditCard,
  FileText,
  Gauge,
  Landmark,
  LayoutGrid,
  Lightbulb,
  Link2,
  ListChecks,
  MonitorSmartphone,
  SlidersHorizontal,
  Receipt,
  Repeat,
  Scale,
  ServerCog,
  Settings2,
  ShieldCheck,
  Sparkles,
  Store,
  Tags,
  Users,
  Wrench,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * `icon` is required, not optional — #190.
 *
 * An optional field would let a nav item ship without one, and the fallback
 * would be the letter glyph this issue exists to remove. Making it required
 * means a new destination cannot be added without choosing a mark for it, and
 * the compiler asks at the point where the answer is known.
 */
/**
 * `badge` is optional, where `icon` is required — #192.
 *
 * The asymmetry is deliberate. Every destination needs a mark; almost none
 * carries a count. #192's stated consumers are Review and Recurring — "a review
 * queue with no count is a queue you have no reason to open" — and those are
 * the needs-review transaction count (M6) and the overdue-recurring count (M7),
 * two different numbers from two different reads.
 *
 * A static `badge` on an item stays legal (the Sidebar's own tests use one), but
 * no item in these tables carries one: a live count is a runtime value, so it
 * arrives as `NavBadges` keyed by path and is applied by `withBadge` at render.
 * The table stays plain data that `App.tsx` can route from without importing a
 * query client.
 *
 * **Recurring's producer is `useRecurringOverdueCount()` (M7, D11, Ruling 50)**:
 * `monthSections(<current local month>).overdue.count`, the Overdue tile's own
 * classifier and number — outflows only, P3's local-date rule, the current
 * month's calendar query rather than the strip's month, and never `/summary`'s
 * UTC `overdue_count`. `useNavBadges` (this directory) reads it and `navBadge`
 * hides it at 0. Review's count (M6) has no producer wired yet.
 *
 * Wiring a badge to whatever count happens to exist — the unread-notifications
 * total was the candidate — puts a figure on an item that has nothing to do
 * with it, and passes its own test while doing so. A producer is named here or
 * there is no badge.
 */
export type NavItem = { label: string; path: string; icon: LucideIcon; badge?: number }
export type NavGroup = { name: string; items: NavItem[] }

/** Live counts by destination path, from `useNavBadges`. */
export type NavBadges = Readonly<Record<string, number | undefined>>

/**
 * D11: a count of 0 is no badge. An overdue badge reading "0" is a pill that
 * says nothing is wrong, in the colour that says something is; absent says it
 * better. Unknown (loading, failed) is absent too.
 */
export function navBadge(count: number | undefined): number | undefined {
  return count === undefined || count <= 0 ? undefined : count
}

/** The item as drawn: its live badge when it has one, else whatever it carried. */
export function withBadge(item: NavItem, badges: NavBadges | undefined): NavItem {
  const live = badges?.[item.path]
  return live === undefined ? item : { ...item, badge: live }
}

/**
 * Four exports, one route table — #205.
 *
 * The sidebar used to be six flat groups. The design puts Settings and Help &
 * support in a **pinned foot** below the group list, and Settings opens a
 * SUB-NAVIGATION column rather than expanding inline. So "which destinations
 * exist" and "where they are drawn" stopped being the same question, and
 * `NAV_GROUPS` could no longer answer both.
 *
 * **`NAV_GROUPS` is the route source as well as the nav source** — `App.tsx`
 * generates a route per item from it, and its docstring says why: "a link
 * added to the nav cannot ship without a destination". So Settings could not
 * be moved by deleting the group. That would have deleted
 * `/settings/security` — a real, shipped screen (#187) — along with four
 * placeholders, and it would have failed as a runtime 404 rather than as a
 * compile error.
 *
 * Hence `ALL_NAV_ITEMS`: the derived union of every destination, whatever
 * surface draws it. `App.tsx` routes from that and nothing else, so a
 * destination cannot exist in a menu and not in the route table. The four
 * source arrays are exported for the surfaces that draw them, but nothing
 * should build a route from one of them — that is the enumeration failure
 * `App.tsx` already records ("the first version of the composition tripwire
 * iterated NAV_GROUPS alone and was blind to all three bare routes").
 */

/**
 * The four scrolling sidebar groups.
 *
 * Was six. Settings and Support moved to `NAV_FOOT` in #205 — the design pins
 * them below the group list, separated by a hairline, and neither expands
 * inline. The M0 spec's "8 groups" counted the pre-login flow and the overlay
 * surfaces, which the sidebar never renders.
 *
 * **Corroborated — NOT confirmed — against the design source, #198.** The
 * distinction is the whole point of this paragraph, and the first draft got it
 * wrong.
 *
 * The committed template declares `<sc-for list="{{ groups }}" as="g"
 * hint-placeholder-count="4">` and `<sc-for list="{{ foot }}" as="n"
 * hint-placeholder-count="2">`. That attribute is **not a list length**. Per
 * the design runtime committed beside it (`support.js`, `walkFor`), it is read
 * only when the bound list is absent *and* the tool is mid-stream, to fabricate
 * skeleton rows — it is never derived from or compared against the real data.
 *
 * And the same file falsifies it where it can be checked: the three widget
 * grids carry hints of 9/10/10 while their actual branch counts are **13/10/10**.
 * On Overview the hint is wrong by four.
 *
 * So four groups and a two-item foot remains **our reconciliation** from #205
 * and #206, exactly as it was. What the source adds is only that a designer
 * authoring the template reached for 4 and 2 — consistent with the shipped
 * shape, not proof of it. The group NAMES and item labels are in the mock-data
 * class lost to the 256 KiB cap and are ours outright.
 *
 * Recorded rather than resolved, which is what #198's second acceptance branch
 * asks for. See `docs/prd/assets/design/README.md`.
 *
 * Labels are unique across groups as well as within them, which is a stronger
 * property than it looks: every test that addresses a destination by its
 * accessible name, and every screen-reader user listing the links, depends on it.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    name: 'Money',
    items: [
      { label: 'Overview', path: '/overview', icon: LayoutGrid },
      { label: 'Cashflow', path: '/cashflow', icon: ArrowLeftRight },
      { label: 'Budget', path: '/budget', icon: Gauge },
      { label: 'Reports', path: '/reports', icon: ChartPie },
      { label: 'Insights', path: '/insights', icon: Lightbulb },
    ],
  },
  {
    name: 'Records',
    items: [
      { label: 'Accounts', path: '/accounts', icon: Landmark },
      { label: 'Connections', path: '/connections', icon: Link2 },
      { label: 'Transactions', path: '/transactions', icon: CreditCard },
      { label: 'Review', path: '/review', icon: ListChecks },
      { label: 'Recurring', path: '/recurring', icon: Repeat },
      // No Import (Q10, issue 535): statement import has no endpoint to call.
    ],
  },
  {
    name: 'Organise',
    items: [
      { label: 'Categories', path: '/categories', icon: Tags },
      { label: 'Merchants', path: '/merchants', icon: Store },
      { label: 'Auto-rules', path: '/rules', icon: Wrench },
      { label: 'Tags', path: '/tags', icon: BadgeCheck },
    ],
  },
  {
    name: 'Household',
    items: [
      { label: 'Members', path: '/household', icon: Users },
      { label: 'Assistant', path: '/assistant', icon: Sparkles },
    ],
  },
]


/**
 * The pinned foot — #205.
 *
 * Recovered from a design screenshot, not from the source file: `foot` lives
 * in the mock-data class in the truncated tail of `TrackE5 Web App.dc.html`
 * and has never been readable (#198). Two items, and Settings is ONE link
 * rather than a group — it opens `SETTINGS_NAV` as a sub-navigation column.
 *
 * Settings points at `/settings`, which redirects to `/settings/profile`.
 *
 * It pointed at `/settings/profile` first, and that was wrong three ways —
 * all three found in review. `NavLink` prefix-matches, so the foot lit up on
 * Profile and NOWHERE else: four of the five settings screens had no active
 * item in the sidebar at all. The two entries also gave ONE path two labels,
 * which put the foot's "Settings" and the sub-nav's "Profile" in conflict —
 * the breadcrumb, the sidebar and the body each picked a different one. And
 * `/settings` is the URL a user types; it rendered the 404.
 *
 * A section link points at its section. The redirect costs one route.
 */
export const NAV_FOOT: NavItem[] = [
  { label: 'Settings', path: '/settings', icon: Settings2 },
  { label: 'Help & support', path: '/help', icon: CircleHelp },
]

/**
 * The settings sub-navigation — #205.
 *
 * Profile · Preferences · Security · Devices · Plan & billing · Developer
 * (Developer outside production only). Integrations was removed by owner
 * decision on 2026-10-20 (F1): the design's Integrations tab has no backing
 * feature, and `/settings/integrations` is now the Not found screen.
 *
 * `Preferences` is its own page — owner decision, #753 — and is also the route
 * the M13 spec lists. The `section` enum in the analytics vocabulary has one
 * value per entry here.
 */
export const SETTINGS_NAV: NavItem[] = [
  { label: 'Profile', path: '/settings/profile', icon: CircleUser },
  { label: 'Preferences', path: '/settings/preferences', icon: SlidersHorizontal },
  { label: 'Security', path: '/settings/security', icon: ShieldCheck },
  { label: 'Devices', path: '/settings/devices', icon: MonitorSmartphone },
  { label: 'Plan & billing', path: '/settings/billing', icon: Receipt },
  /**
   * Developer — #449. The backend switch, in every build EXCEPT production.
   *
   * Spread from an inline MODE check rather than filtered afterwards, so a
   * production build folds this to `...[]` and neither the path nor the label
   * is emitted. Being in this table is also what routes it: `App.tsx`
   * generates the settings routes from `SETTINGS_NAV`, so production has no
   * `/settings/developer` route to reach, not just no link to it.
   * `backendOverride.build.test.ts` checks the built bundle for the path.
   */
  ...(import.meta.env.MODE !== 'production'
    ? [{ label: 'Developer', path: '/settings/developer', icon: ServerCog }]
    : []),
]

/**
 * Reachable from Help & support. Not drawn in the sidebar, but every one needs
 * a route, which is exactly the case `ALL_NAV_ITEMS` exists for.
 */
export const SUPPORT_NAV: NavItem[] = [
  // No 'Help centre' entry: `/help` is the foot's "Help & support" and one
  // path may carry only one label. Two labels for `/help` had the sidebar,
  // the breadcrumb and the body each announcing a different name for the same
  // screen — found in review.
  { label: 'Legal', path: '/legal', icon: Scale },
  { label: 'States reference', path: '/states', icon: FileText },
]

/**
 * Every destination, deduplicated by path — the ONLY thing routes are built
 * from.
 *
 * Deduplicated because two surfaces legitimately point at one path: the foot's
 * Settings and the sub-nav's Profile are both `/settings/profile`, and Help &
 * support and Help centre are both `/help`. Two `<Route>` elements for one
 * path is not an error React Router reports — it silently renders the first —
 * so the dedupe is what keeps that from becoming a debugging session.
 */
export const ALL_NAV_ITEMS: NavItem[] = [
  ...NAV_GROUPS.flatMap((g) => g.items),
  ...NAV_FOOT,
  ...SETTINGS_NAV,
  ...SUPPORT_NAV,
].filter((item, i, all) => all.findIndex((o) => o.path === item.path) === i)

/**
 * The phone tab bar's five destinations — #109.
 *
 * Product overview §6 rule 3: *"Bottom tab bar on phone, mirroring the iOS
 * five: Overview, Accounts, Transactions, Budget, Cashflow. Everything else
 * lives behind a 'More' sheet."* The order is part of that — a user moving
 * between the two apps reaches for a position, not a label.
 *
 * Paths, not items, because the labels and icons must come from the nav model
 * rather than be restated here. `nav.test.ts` already pinned these five before
 * the surface existed; that test now points at this constant instead of its own
 * copy of the list.
 */
export const PHONE_TAB_PATHS = [
  '/overview',
  '/accounts',
  '/transactions',
  '/budget',
  '/cashflow',
] as const

/**
 * Resolve tab paths against the nav model.
 *
 * **Throws rather than filtering.** A `.find()` returning `undefined` for a
 * renamed path would put a hole in the bar — a tab that renders nothing and
 * navigates nowhere — and `.filter(Boolean)` would silently ship four tabs
 * where the spec says five. At module scope a throw is a red build, which is
 * the only failure mode here anybody would notice.
 *
 * Exported for its own test: the failure is the interesting behaviour and it is
 * unreachable once the constant below has been evaluated successfully.
 */
export function resolveTabs(paths: readonly string[]): NavItem[] {
  return paths.map((path) => {
    const item = ALL_NAV_ITEMS.find((i) => i.path === path)
    if (!item) throw new Error(`nav: phone tab "${path}" names no destination`)
    return item
  })
}

export const PHONE_TABS: NavItem[] = resolveTabs(PHONE_TAB_PATHS)

/**
 * Everything the tab bar does not carry — #109.
 *
 * Derived by subtraction, so "everything else lives behind a 'More' sheet" is a
 * property the build maintains rather than a second list somebody keeps in
 * step. Adding a destination to `NAV_GROUPS` puts it in the sheet with no
 * further edit; promoting one to a tab removes it from the sheet in the same
 * commit.
 *
 * The group SHAPE is kept — Money, Records, Organise, Household, then the foot
 * — because the sheet holds 14 destinations and a flat list of 14 is a wall.
 * The sidebar groups them for the same reason and the two surfaces should not
 * teach different structures. A group emptied by promotion drops out rather
 * than rendering an empty heading.
 *
 * `SETTINGS_NAV` and `SUPPORT_NAV` are deliberately absent: those are reached
 * THROUGH `/settings` and `/help`, both of which the foot carries, and
 * flattening a sub-navigation into its parent's sheet would give
 * `/settings/profile` two routes to it from one surface.
 */
export const MORE_GROUPS: NavGroup[] = [
  ...NAV_GROUPS.map((g) => ({
    name: g.name,
    items: g.items.filter((i) => !PHONE_TABS.includes(i)),
  })),
  // NOT 'Support' — the foot is Settings and Help & support, and naming that
  // pair Support names a group after half its contents, teaching a structure
  // the sidebar does not have. NOT 'More' either, which was the first
  // correction: it put a group called More inside a sheet labelled "More
  // destinations" opened by a button labelled More, so the word named three
  // different things at once. The sidebar draws this pair as an unnamed pinned
  // foot; a sheet cannot do unnamed, so it takes an accurate name.
  { name: 'Settings & help', items: NAV_FOOT },
].filter((g) => g.items.length > 0)
